import { useMemo, useState } from "react";
import { works } from "@/mocks/gallery";

type Filter = "全部" | "插画" | "壁纸" | "画集";

const filters: Filter[] = ["全部", "插画", "壁纸", "画集"];

export default function FeaturedWorks() {
  const [active, setActive] = useState<Filter>("全部");
  const [lightbox, setLightbox] = useState<(typeof works)[number] | null>(null);

  const list = useMemo(
    () => (active === "全部" ? works : works.filter((w) => w.category === active)),
    [active]
  );

  return (
    <section id="works" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-14 md:px-6 md:py-20">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-primary-600">
            <i className="ri-sparkling-2-line"></i>
            Featured Works
          </span>
          <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
            精选作品
          </h2>
          <p className="mt-2 max-w-xl text-sm text-foreground-600">
            从插画到壁纸,从日常到冒险,每一幅都是画师们最用心的呈现。
          </p>
        </div>

        <div className="flex rounded-full bg-background-200/60 p-1">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                active === f
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((w) => (
          <article
            key={w.id}
            onClick={() => setLightbox(w)}
            className="group relative cursor-pointer overflow-hidden rounded-lg bg-background-100"
          >
            <div className={`w-full ${w.ratio} overflow-hidden`}>
              <img
                src={w.image}
                alt={`${w.title}·娜美同人插画`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>

            <span className="absolute left-3 top-3 rounded-full bg-black/40 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
              {w.category}
            </span>

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 pt-10">
              <h3 className="font-heading text-base text-background-50">{w.title}</h3>
              <div className="mt-1 flex items-center justify-between text-xs text-background-50/80">
                <span className="flex items-center gap-1">
                  <i className="ri-user-smile-line"></i>
                  {w.artist}
                </span>
                <span className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-3-line"></i>
                    {w.likes >= 1000 ? `${(w.likes / 1000).toFixed(1)}k` : w.likes}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {w.views >= 1000 ? `${(w.views / 1000).toFixed(1)}k` : w.views}
                  </span>
                </span>
              </div>
            </div>

            <span className="absolute right-3 top-3 hidden h-9 w-9 items-center justify-center rounded-full bg-background-50/90 text-primary-600 opacity-0 transition-opacity group-hover:opacity-100 md:flex">
              <i className="ri-zoom-in-line"></i>
            </span>
          </article>
        ))}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative max-h-[88vh] w-full max-w-3xl overflow-hidden rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.image}
              alt={`${lightbox.title}·娜美同人插画`}
              className="max-h-[74vh] w-full object-contain"
            />
            <div className="flex items-center justify-between bg-background-50 px-5 py-3.5">
              <div>
                <h3 className="font-heading text-base text-foreground-950">{lightbox.title}</h3>
                <p className="mt-0.5 text-xs text-foreground-600">
                  {lightbox.artist} · {lightbox.tag} · 分类:{lightbox.category}
                </p>
              </div>
              <div className="flex items-center gap-3 text-sm text-foreground-700">
                <span className="flex items-center gap-1">
                  <i className="ri-heart-3-fill text-primary-500"></i>
                  {lightbox.likes >= 1000 ? `${(lightbox.likes / 1000).toFixed(1)}k` : lightbox.likes}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {lightbox.views >= 1000 ? `${(lightbox.views / 1000).toFixed(1)}k` : lightbox.views}
                </span>
                <button
                  onClick={() => setLightbox(null)}
                  className="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-foreground-800 transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer"
                  aria-label="关闭"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}